// Screen outline for NoVA — a compact, redacted plain-text description of
// what the user is looking at RIGHT NOW (which tab is open, the headings,
// the buttons, the empty state, the red banner), captured from the rendered
// DOM when a turn is sent. The console catalog is the map; this is the
// territory. Same file in the control plane and the App Kit foundation —
// edit both.
//
// Conventions a page can use (also honoured by generated kit screens):
//   data-nova-note="Step 2 of 5: waiting for the research run"  → quoted verbatim
//   data-nova-ignore                                              → subtree skipped
//
// Privacy: password / email / tel / payment inputs and anything that looks
// like a secret, card number or email are emitted as [redacted]; other
// values are clipped to 40 chars. Nothing here reaches a model until the
// caller sends it; the backend applies its own cap (agentos.screen).

import { getBuildFreshness } from './buildFreshness.js';

const IGNORE = '[data-nova-ignore], .cp-panel, .cp-fab, .va-panel, .va-launcher, .va-rail, .anr, script, style, svg, canvas, noscript, template';
const CONTAINER = '[role="alert"], [role="status"], .cs-banner, .cs-empty, [role="tablist"], .cs-tabs, table';
const SECRET_RE = /pass|secret|token|api[-_ ]?key|card|ssn|cvv|cvc|otp|pin\b/i;
const CARD_RE = /^[\d\s-]{13,23}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const text = (el, n = 160) => String(el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, n);
const clip = (s, n) => (s.length > n ? `${s.slice(0, n)}…` : s);

function hidden(el, win) {
  if (el.hidden || el.getAttribute('aria-hidden') === 'true') return true;
  if (el.tagName === 'DIALOG' && !el.open) return true;
  try {
    const cs = win.getComputedStyle(el);
    return cs.display === 'none' || cs.visibility === 'hidden';
  } catch { return false; }
}

const below = (el, win) => {
  try {
    const r = el.getBoundingClientRect();
    return r.height > 0 && r.top > (win.innerHeight || 0);
  } catch { return false; }
};

function fieldLabel(el, doc) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();
  const by = el.getAttribute('aria-labelledby');
  if (by) { const t = text(doc.getElementById(by), 80); if (t) return t; }
  if (el.id) {
    const l = Array.from(doc.querySelectorAll('label[for]')).find((x) => x.getAttribute('for') === el.id);
    if (l) return text(l, 80);
  }
  const wrap = el.closest('label');
  if (wrap) {
    const t = text(wrap, 80).replace(text(el, 80), '').trim();
    if (t) return t;
  }
  return el.getAttribute('placeholder') || el.getAttribute('name') || '';
}

function fieldValue(el) {
  const tag = el.tagName;
  const type = (el.getAttribute('type') || (tag === 'TEXTAREA' ? 'textarea' : tag === 'SELECT' ? 'select' : 'text')).toLowerCase();
  if (type === 'checkbox' || type === 'radio') return { type, value: el.checked ? 'checked' : 'unchecked' };
  if (tag === 'SELECT') {
    const opt = el.options?.[el.selectedIndex];
    return { type, value: opt ? text(opt, 40) : '' };
  }
  const raw = String(el.value || '');
  const hint = `${el.getAttribute('name') || ''} ${el.id || ''} ${el.getAttribute('autocomplete') || ''} ${el.getAttribute('aria-label') || ''}`;
  const secret = ['password', 'email', 'tel'].includes(type)
    || /^cc-/.test(el.getAttribute('autocomplete') || '')
    || SECRET_RE.test(hint)
    || (raw && (CARD_RE.test(raw) || EMAIL_RE.test(raw.trim())));
  if (!raw) return { type, value: '(empty)' };
  return { type, value: secret ? '[redacted]' : clip(raw.replace(/\s+/g, ' '), 40) };
}

function tabsLine(el) {
  const tabs = Array.from(el.querySelectorAll('[role="tab"], button, a')).filter((t) => text(t));
  if (!tabs.length) return '';
  const parts = tabs.slice(0, 16).map((t) => {
    const on = t.getAttribute('aria-selected') === 'true' || t.getAttribute('aria-current') || t.classList.contains('active') || t.classList.contains('is-active') || t.classList.contains('on');
    const label = text(t, 40);
    return on ? `[${label}]` : label;
  });
  return `Tabs: ${parts.join(' · ')}`;
}

function tableLine(el) {
  const heads = Array.from(el.querySelectorAll('th')).map((h) => text(h, 30)).filter(Boolean).slice(0, 12);
  const rows = el.querySelectorAll('tbody tr').length || Math.max(0, el.querySelectorAll('tr').length - (heads.length ? 1 : 0));
  const cap = el.querySelector('caption');
  return `Table${cap ? ` "${text(cap, 60)}"` : ''}: ${heads.join(' | ') || '(no headers)'} (${rows} row${rows === 1 ? '' : 's'})`;
}

function dialogTitle(el, doc) {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();
  const by = el.getAttribute('aria-labelledby');
  if (by) { const t = text(doc.getElementById(by), 80); if (t) return t; }
  return text(el.querySelector('h1,h2,h3,h4,[role="heading"]'), 80) || text(el, 80);
}

// captureScreenOutline({ root?, maxChars = 3500, win = window }) → string
// ('' when there is no document). Never throws — a capture failure is a
// turn without screen context, not a failed turn.
export function captureScreenOutline({ root = null, maxChars = 3500, win = typeof window !== 'undefined' ? window : null } = {}) {
  try {
    const doc = win?.document;
    if (!doc?.body) return '';
    const scope = root || doc.querySelector('main') || doc.body;
    const lines = [];
    const buttons = [];
    const links = [];
    const badges = [];
    const notes = [];
    const seen = new Set();
    const push = (arr, s) => { if (s && !seen.has(`${arr}:${s}`)) { seen.add(`${arr}:${s}`); arr.push(s); } };

    // Open dialogs first — they are what the user is looking at.
    for (const d of Array.from(doc.querySelectorAll('[role="dialog"], dialog'))) {
      if (d.closest(IGNORE) || hidden(d, win) || d.closest('.cp-panel, .va-panel, .anr')) continue;
      lines.push(`Dialog open: ${dialogTitle(d, doc)}`);
    }

    const walker = doc.createTreeWalker(scope, win.NodeFilter?.SHOW_ELEMENT ?? 1);
    let el = scope;
    let skipUntil = null;
    while (el) {
      if (skipUntil && skipUntil.contains(el)) { el = walker.nextNode(); continue; }
      skipUntil = null;
      if (el !== scope && (el.matches(IGNORE) || hidden(el, win))) { skipUntil = el; el = walker.nextNode(); continue; }

      const tag = el.tagName;
      const role = el.getAttribute('role') || '';
      const pos = below(el, win) ? ' (below)' : '';
      const note = el.getAttribute('data-nova-note');
      if (note) push(notes, note.trim().slice(0, 200));

      if (/^H[1-6]$/.test(tag) || role === 'heading') {
        const level = role === 'heading' ? Number(el.getAttribute('aria-level') || 2) : Number(tag[1]);
        const t = text(el, 120);
        if (t) lines.push(`${'#'.repeat(Math.min(level, 4))} ${t}${pos}`);
      } else if (el.matches(CONTAINER)) {
        if (role === 'tablist' || el.classList.contains('cs-tabs')) lines.push(tabsLine(el));
        else if (tag === 'TABLE') lines.push(`${tableLine(el)}${pos}`);
        else {
          const kind = role === 'alert' ? 'Alert' : role === 'status' ? 'Status' : el.classList.contains('cs-empty') ? 'Empty state' : 'Banner';
          const t = text(el, 220);
          if (t) lines.push(`${kind}: ${t}${pos}`);
        }
        skipUntil = el;
      } else if (el.getAttribute('aria-current') && !el.closest('[role="tablist"], .cs-tabs')) {
        const t = text(el, 60);
        if (t) lines.push(`Current: ${t}`);
      } else if (tag === 'BUTTON' || role === 'button' || (tag === 'INPUT' && /^(button|submit|reset)$/i.test(el.type))) {
        const t = text(el, 50) || el.getAttribute('aria-label') || el.value || el.getAttribute('title') || '';
        if (t) push(buttons, `${t.trim()}${el.disabled ? ' (disabled)' : ''}`);
        skipUntil = el;
      } else if (tag === 'A' && el.getAttribute('href')) {
        const t = text(el, 50) || el.getAttribute('aria-label') || '';
        if (t) push(links, t.trim());
        skipUntil = el;
      } else if ((tag === 'INPUT' && !/^(hidden|button|submit|reset|image)$/i.test(el.type)) || tag === 'TEXTAREA' || tag === 'SELECT') {
        const { type, value } = fieldValue(el);
        const label = fieldLabel(el, doc);
        lines.push(`Field "${label || type}" (${type}): ${value}${pos}`);
      } else if (el.classList.contains('cs-badge') || el.classList.contains('cs-chip') || (role === 'tab' && !el.closest('[role="tablist"], .cs-tabs'))) {
        const t = text(el, 40);
        if (t) push(badges, t);
        skipUntil = el;
      }
      el = walker.nextNode();
    }

    if (notes.length) lines.unshift(...notes.map((n) => `Note: ${n}`));
    // The tab's build vs the deployed one (utils/buildFreshness.js): a stale
    // UI explains a lot of "this looks broken" — NoVA says "reload" first.
    const fresh = getBuildFreshness();
    if (fresh.stale) lines.unshift('App version: OUT OF DATE — a newer build is live; the page should be reloaded (the "Reload" button in the notice at the top, or the browser reload) before anything else is diagnosed.');
    if (badges.length) lines.push(`Badges: ${badges.slice(0, 20).join(' · ')}`);
    if (buttons.length) lines.push(`Buttons: ${buttons.slice(0, 40).join(' · ')}`);
    if (links.length) lines.push(`Links: ${links.slice(0, 30).join(' · ')}`);
    const sel = String(win.getSelection?.()?.toString() || '').replace(/\s+/g, ' ').trim();
    if (sel) lines.push(`User highlighted: ${clip(sel, 300)}`);

    let out = lines.filter(Boolean).join('\n');
    if (out.length > maxChars) out = `${out.slice(0, Math.max(0, maxChars - 16))}\n… (truncated)`;
    return out;
  } catch {
    return '';
  }
}

export default captureScreenOutline;
